<template>
  <div id="app-main" class="app__main" :class="fontClass">
    <input-state-toggle class="app__font-toggle" :value="font" :states="['system', 'fira', 'inter', 'google']" @update:value="updateFontClass($event)" />

    <!-- NAVIGATION -->

    <!-- CONTENT -->
    <div class="app__content">
      <router-view class="view_current" />
    </div>

    <!-- messages -->
    <simulation-message />

    <!-- all overlaying stuff-->
    <text-editor-popup />
    <subscription-ordering-popup />
    <imprint-popup />
    <loading-message
      :visible="state.loading"
      :progress-tracker="state.loadingProgressTracker || undefined"
    />
    <login-guard-popup />
    <new-alert v-for="alert in alerts" :key="alert.id" :alert="alert" />
    <right-click-menu />
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick
} from "vue";
import { useToast } from "vue-toastification";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import globalErrorHandler from "@core/errors/ErrorHandler";
import { NoInternetError } from "@core/errors/Errors";
import eventHub from "@/helpers/utility/Events";

import { useLoadedModel } from "@editor/store/loadedModel.store";

import RightClickMenu from "@core/ui/RightClickMenu.vue";
import TextEditorPopup from "@core/ui/TextEditorPopup.vue";
import LoadingMessage from "@core/ui/LoadingMessage.vue";
import NewAlert from "@core/ui/alert/AlertDialog.vue";

import SubscriptionOrderingPopup from "@manager/purchasing/SubscriptionOrderingPopup.vue";
import LoginGuardPopup from "@manager/auth/LoginGuardPopup.vue";
import ImprintPopup from "@core/imprint/ImprintPopup.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";

import SimulationMessage from "@editor/views/simulation/SimulationMessage.vue";

import { alerts, dismissTopAlert } from "@core/ui/alert/alert";
import { useUndo } from "@editor/store/undo/undo.store";
import { saveModel } from "@/api/SaveService";
import { useSettingsStore } from "@editor/store/settings.store";
import { showUpdateNotificationIfNeeded } from "@core/functions/updateNotification";

import type { ModelSettings } from "@editor/store/settings.store";
import { useColor } from "@/helpers/utility/Color";

const toast = useToast();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { loadedModel } = useLoadedModel();

// ====================================
// SAFARI WARNING

onMounted(() => {
  const browser = getBrowserSpecs();

  /**
   * Show a warning message if the user is using Safari 14 or older.
   */
  if (browser.name === "Safari") {
    const version = browser.version;
    if (version < 14) {
      nextTick(() => {
        toast.warning(t("messages.safariWarning", { version: version }), {
          timeout: false
        });
      });
    }
  }
});

/**
 * Get information about the user's browser.
 */
function getBrowserSpecs() {
  const userAgent = navigator.userAgent;
  let name;
  let version;

  if (userAgent.indexOf("Firefox") > -1) {
    name = "Firefox";
    version = parseInt(userAgent.match(/Firefox\/(\d+)/)?.[1] || "0");
  } else if (userAgent.indexOf("Edg") > -1) {
    name = "Edge";
    version = parseInt(userAgent.match(/Edg\/(\d+)/)?.[1] || "0");
  } else if (userAgent.indexOf("Chrome") > -1) {
    name = "Chrome";
    version = parseInt(userAgent.match(/Chrome\/(\d+)/)?.[1] || "0");
  } else if (userAgent.indexOf("Safari") > -1) {
    name = "Safari";
    version = parseInt(userAgent.match(/Version\/(\d+)/)?.[1] || "0");
  } else {
    name = "Unknown";
    version = 0;
  }

  return { name, version };
}

// ====================================
// KEYBOARD SHORTCUTS

onMounted(() => {
  window.addEventListener("keydown", onKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKey);
});

function onKey(event: KeyboardEvent) {
  let eventHandled = false;

  if (event.key) {
    const lowerCaseKey = event.key.toLowerCase();

    const CMD_PRESSED = event.ctrlKey || event.metaKey;

    const activeElement = document.activeElement;
    const isInTextInput =
      (event.target instanceof HTMLElement &&
        event.target.classList.contains("ProseMirror")) ||
      activeElement?.tagName === "TEXTAREA" ||
      (activeElement instanceof HTMLElement &&
        activeElement?.isContentEditable === true &&
        activeElement?.dataset?.useAppUndo != "true");

    // redo/undo only when not in text input
    if (!isInTextInput) {
      // CTRL + Z  | undo
      if (CMD_PRESSED && lowerCaseKey === "z") {
        undo();
        eventHandled = true;
      }

      // CTRL + Y  | redo
      else if (CMD_PRESSED && lowerCaseKey === "y") {
        redo();
        eventHandled = true;
      }
    }

    // CTRL + S  | save
    if (lowerCaseKey === "s" && CMD_PRESSED) {
      if (event.shiftKey) {
        saveAs();
      } else {
        save();
      }
      eventHandled = true;
    }

    if (event.key == "Escape") {
      if (dismissTopAlert()) {
        eventHandled = true;
      }
    }
  }

  if (eventHandled) {
    event.preventDefault();
  }
}

function undo() {
  useUndo().undo("model");
}
function redo() {
  useUndo().redo("model");
}
async function save() {
  if (loadedModel.isLocked || !loadedModel.permissions.canUpdate) return saveAs();
  return saveModel();
}
async function saveAs() {
  if (!loadedModel.permissions.canFork) return;
  return saveModel({ asNewModel: true });
}

// ====================================
// UPDATE NOTIFICATION

onMounted(() => {
  // Pack the update notification in the next tick
  // to make sure that the toast is beeing shown.
  nextTick(() => {
    showUpdateNotificationIfNeeded();
  });
});

router.afterEach(() => {
  nextTick(() => {
    showUpdateNotificationIfNeeded();
  });
});

// ====================================
// LOADING

const state = reactive({
  loading: false,
  loadingProgressTracker: null as null | {
    [requestId: string]: { percent: number };
  }
});

function onLoadingStarted({ progressTracker = null } = {}) {
  state.loading = true;
  state.loadingProgressTracker = progressTracker;
}
function onLoadingEnded() {
  state.loading = false;
  state.loadingProgressTracker = null;
}
eventHub.on("loading-started", onLoadingStarted);
eventHub.on("loading-ended", onLoadingEnded);

onBeforeUnmount(() => {
  eventHub.off("loading-started", onLoadingStarted);
  eventHub.off("loading-ended", onLoadingEnded);
});

// ====================================
// ERROR HANDLING

function handleErrors(error: Error | ErrorEvent) {
  if (error instanceof NoInternetError) {
    toast.error(t("errors.noConnection"));
    return true;
  }
  return false;
}

globalErrorHandler.add(handleErrors);
onBeforeUnmount(() => {
  globalErrorHandler.remove(handleErrors);
});

// ====================================
// STYLES

const isInEditor = computed(() => {
  return route.meta.isEditorView === true;
});

const projectSettings = computed((): Partial<ModelSettings> => {
  if (!isInEditor.value || loadedModel.id == null) return {};
  return useSettingsStore();
});

const appColors = computed(() => {
  const settings = projectSettings.value;
  const colors: Record<string, string | number> = {};
  if (settings.colorPrimary != null) {
    colors["--color-primary"] = settings.colorPrimary;
  }
  if (settings.colorPrimaryHue != null) {
    colors["--color-primary-h"] = settings.colorPrimaryHue;
  }
  if (settings.colorPrimaryContrast != null) {
    colors["--color-primary-contrast"] = settings.colorPrimaryContrast;
  }
  if (settings.colorPrimaryTransparent != null) {
    colors["--color-primary-transparent"] =
    settings.colorPrimaryTransparent;
  }
  if (settings.colorPrimaryLight != null) {
    colors["--color-primary-light"] = settings.colorPrimaryLight;
  }
  if (settings.colorPrimaryDark != null) {
    colors["--color-primary-dark"] = settings.colorPrimaryDark;
  }

  if (settings.colorGrey != null) {
    colors["--color-grey"] = settings.colorGrey;
  }
  if (settings.colorGreyDark != null) {
    colors["--color-grey-dark"] = settings.colorGreyDark;
  }
  if (settings.colorGreyDarker != null) {
    colors["--color-grey-darker"] = settings.colorGreyDarker;
  }
  if (settings.colorGreyLight != null) {
    colors["--color-grey-light"] = settings.colorGreyLight;
  }
  if (settings.colorGreyLighter != null) {
    colors["--color-grey-lighter"] = settings.colorGreyLighter;
  }
  return colors;
});

const appColorMode = useColor();

watch(
  () => projectSettings.value.darkModeOverride,
  (active) => {
    if (active) {
      appColorMode.setDark(true);
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else if (active === false) {
      appColorMode.setDark(false);
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    } else {
      appColorMode.setDark(null);
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.remove("light");
    }
  },
  { immediate: true }
);

watch(
  appColors,
  (styles) => {
    const root = document.documentElement;
    let styleDefinitions = [];
    for (const style in styles) {
      styleDefinitions.push(`${style}: ${styles[style]}`);
    }
    root.setAttribute("style", styleDefinitions.join(";"));
  },
  { immediate: true }
);

// ====================================
// FONTS

const font = ref('system');

const fontClasses: Record<string, string> = {
  system: "-font-system",
  golos: "-font-golos",
  asap: "-font-asap",
  fira: "-font-fira",
  inter: "-font-inter",
  google: "-font-google"
}

const fontClass = computed(() => {
  return fontClasses[font.value] || "-font-system";
});

function updateFontClass(newFont: string) {
  font.value = newFont;
  const allClasses = Object.values(fontClasses);
  document.documentElement.classList.remove(...allClasses);
  document.documentElement.classList.add(fontClass.value);
}
</script>

<style lang="scss">
.app__main {
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: var(--color-background);
  z-index: 0;
  color: var(--color-text);

  font-family: var(--font-family-text);
}

.app__content {
  overflow: hidden;
  height: 100%;
  position: relative;
  z-index: 0;
}

.app__font-toggle {
  position: absolute;
  top: 5px;
  right: 250px;
  width: 200px;
  z-index: 100;
  display: none;
}

.view_current {
  height: 100%;
}

// &.-font-system {
//   --font-family-text: "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   --font-family-ui: "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
// }
// &.-font-golos {
//   --font-family-text: "GolosText", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   --font-family-ui: "GolosUI", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
// }
// &.-font-asap {
//   --font-family-text: "Asap", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   --font-family-ui: "Asap", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
// }
// .-font-fira {
//   --font-family-text: "Fira Sans", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   --font-family-ui: "Fira Sans", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
// }
// .-font-inter {
//   --font-family-text: "Inter", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   --font-family-ui: "Inter", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
// }
// .-font-google {
//   --font-family-text: "Google Sans Flex", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   --font-family-ui: "Google Sans Flex", -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, Tahoma, Geneva, Verdana, sans-serif;
//   font-optical-sizing: auto;
//   font-style: normal;
//   font-variation-settings:
//     "slnt" 0,
//     "wdth" 100,
//     "GRAD" 0,
//     "ROND" 0;
// }
</style>
